class YagchoMediaGallery extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.dialog = this.querySelector('[data-yagcho-lightbox-dialog]');
    this.image = this.querySelector('[data-yagcho-lightbox-image]');
    this.media = this.querySelector('[data-yagcho-lightbox-media]');
    this.zoomButton = this.querySelector('[data-yagcho-lightbox-zoom]');
    this.thumbnails = this.querySelector('[data-yagcho-lightbox-thumbnails]');
    this.previousButton = this.querySelector('[data-yagcho-lightbox-previous]');
    this.nextButton = this.querySelector('[data-yagcho-lightbox-next]');
    this.closeButton = this.querySelector('[data-yagcho-lightbox-close]');
    this.status = this.querySelector('[data-yagcho-lightbox-status]');
    this.galleryId = this.dataset.galleryId;
    this.items = [];
    this.activeIndex = 0;
    this.isZoomed = false;
    this.mediaPointerStart = null;
    this.pointerStartX = null;

    if (!this.dialog || !this.image || !this.media || !this.zoomButton || !this.thumbnails || !this.galleryId) return;

    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleDialogClick = this.handleDialogClick.bind(this);
    this.handleDialogClose = this.handleDialogClose.bind(this);
    this.handleMediaPointerDown = this.handleMediaPointerDown.bind(this);
    this.handleMediaPointerUp = this.handleMediaPointerUp.bind(this);
    this.handleTriggerPointerDown = this.handleTriggerPointerDown.bind(this);
    this.handleTriggerPointerMove = this.handleTriggerPointerMove.bind(this);

    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('pointerdown', this.handleTriggerPointerDown);
    document.addEventListener('pointermove', this.handleTriggerPointerMove);
    this.closeButton.addEventListener('click', () => this.dialog.close());
    this.previousButton.addEventListener('click', () => this.showItem(this.activeIndex - 1));
    this.nextButton.addEventListener('click', () => this.showItem(this.activeIndex + 1));
    this.zoomButton.addEventListener('click', () => this.setZoom(!this.isZoomed));
    this.media.addEventListener('pointerdown', this.handleMediaPointerDown);
    this.media.addEventListener('pointerup', this.handleMediaPointerUp);
    this.media.addEventListener('pointercancel', () => {
      this.mediaPointerStart = null;
    });
    this.dialog.addEventListener('click', this.handleDialogClick);
    this.dialog.addEventListener('close', this.handleDialogClose);

    const stage = this.querySelector('[data-yagcho-lightbox-stage]');
    stage.addEventListener('pointerdown', (event) => {
      if (this.isZoomed) return;
      this.pointerStartX = event.clientX;
    });
    stage.addEventListener('pointerup', (event) => {
      if (this.isZoomed) return;
      if (this.pointerStartX === null) return;

      const distance = event.clientX - this.pointerStartX;
      this.pointerStartX = null;
      if (Math.abs(distance) < 48 || this.items.length < 2) return;

      this.showItem(distance < 0 ? this.activeIndex + 1 : this.activeIndex - 1);
    });
    stage.addEventListener('pointercancel', () => {
      this.pointerStartX = null;
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('pointerdown', this.handleTriggerPointerDown);
    document.removeEventListener('pointermove', this.handleTriggerPointerMove);
  }

  handleDocumentClick(event) {
    const trigger = event.target.closest('[data-yagcho-gallery-open]');
    if (!trigger || trigger.dataset.yagchoGalleryOpen !== this.galleryId) return;

    if (this.triggerPointer?.trigger === trigger && this.triggerPointer.moved) {
      event.preventDefault();
      this.triggerPointer = null;
      return;
    }

    this.triggerPointer = null;
    event.preventDefault();
    this.open(trigger);
  }

  handleTriggerPointerDown(event) {
    const trigger = event.target.closest('[data-yagcho-gallery-open]');
    if (!trigger || trigger.dataset.yagchoGalleryOpen !== this.galleryId) return;

    this.triggerPointer = { moved: false, trigger, x: event.clientX, y: event.clientY };
  }

  handleTriggerPointerMove(event) {
    if (!this.triggerPointer || this.triggerPointer.moved) return;

    const horizontalDistance = Math.abs(event.clientX - this.triggerPointer.x);
    const verticalDistance = Math.abs(event.clientY - this.triggerPointer.y);
    if (horizontalDistance > 12 || verticalDistance > 12) this.triggerPointer.moved = true;
  }

  handleKeydown(event) {
    if (!this.dialog.open || this.items.length < 2) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.showItem(this.activeIndex - 1);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.showItem(this.activeIndex + 1);
    }
  }

  handleDialogClick(event) {
    if (event.target === this.dialog) this.dialog.close();
  }

  handleDialogClose() {
    this.setZoom(false);
    if (this.lastTrigger && document.contains(this.lastTrigger)) this.lastTrigger.focus();
    this.lastTrigger = null;
  }

  handleMediaPointerDown(event) {
    if (!this.isZoomed || !window.matchMedia('(max-width: 749px)').matches) return;
    this.mediaPointerStart = { x: event.clientX, y: event.clientY };
  }

  handleMediaPointerUp(event) {
    if (!this.isZoomed || !this.mediaPointerStart || !window.matchMedia('(max-width: 749px)').matches) return;

    const horizontalDistance = Math.abs(event.clientX - this.mediaPointerStart.x);
    const verticalDistance = Math.abs(event.clientY - this.mediaPointerStart.y);
    this.mediaPointerStart = null;

    if (horizontalDistance < 12 && verticalDistance < 12) this.setZoom(false);
  }

  open(trigger) {
    this.items = this.collectItems();
    if (!this.items.length) return;

    this.lastTrigger = trigger;
    const triggerImage = trigger.querySelector('img');
    const triggerSource = this.normalizedSource(triggerImage);
    const directIndex = this.items.findIndex((item) => item.source === trigger);
    const matchingIndex = this.items.findIndex((item) => item.src === triggerSource);
    this.showItem(directIndex >= 0 ? directIndex : Math.max(matchingIndex, 0));

    if (!this.dialog.open) {
      this.dialog.showModal();
      this.closeButton.focus();
    }
  }

  collectItems() {
    const sourceSelector = `[data-yagcho-gallery-source="${this.galleryId}"]`;
    const mediaSelector = `[data-yagcho-gallery-media="${this.galleryId}"]`;
    const sourceItems = Array.from(document.querySelectorAll(sourceSelector))
      .filter((source) => window.getComputedStyle(source).display !== 'none')
      .map((source) => {
        const image = source.querySelector('img');
        return {
          alt: image ? image.alt : '',
          source,
          src: this.normalizedSource(image),
        };
      })
      .filter((item) => item.src);
    const mediaItems = Array.from(document.querySelectorAll(mediaSelector))
      .map((source) => ({
        alt: source.dataset.alt || '',
        source,
        src: this.normalizedSource(source.dataset.image),
      }))
      .filter((item) => item.src);

    if (!mediaItems.length) return this.uniqueItems(sourceItems);

    const activeSource = sourceItems[0];
    const mainMatchesMedia = activeSource && mediaItems.some((item) => item.src === activeSource.src);
    return this.uniqueItems(mainMatchesMedia ? mediaItems : [activeSource, ...mediaItems]);
  }

  uniqueItems(items) {
    const seen = new Set();
    return items.filter((item) => {
      if (!item || !item.src || seen.has(item.src)) return false;
      seen.add(item.src);
      return true;
    });
  }

  normalizedSource(source) {
    const value = typeof source === 'string' ? source : source && (source.currentSrc || source.src);
    if (!value) return '';

    try {
      return new URL(value, document.baseURI).href;
    } catch (error) {
      return value;
    }
  }

  showItem(index) {
    const count = this.items.length;
    if (!count) return;

    this.activeIndex = (index + count) % count;
    const item = this.items[this.activeIndex];
    this.setZoom(false);
    this.image.src = item.src;
    this.image.alt = item.alt;
    this.renderThumbnails();
    this.updateControls();
    this.status.textContent = this.dataset.statusTemplate.replace('__index__', this.activeIndex + 1);
  }

  setZoom(zoomed) {
    this.isZoomed = zoomed;
    this.media.classList.toggle('is-zoomed', zoomed);
    this.zoomButton.setAttribute('aria-pressed', String(zoomed));

    if (!zoomed) {
      this.media.scrollLeft = 0;
      this.media.scrollTop = 0;
      return;
    }

    requestAnimationFrame(() => {
      if (!this.isZoomed) return;
      this.media.scrollLeft = (this.media.scrollWidth - this.media.clientWidth) / 2;
      this.media.scrollTop = (this.media.scrollHeight - this.media.clientHeight) / 2;
    });
  }

  renderThumbnails() {
    const thumbnailLabel = this.dataset.thumbnailLabel;
    this.thumbnails.textContent = '';

    this.items.forEach((item, index) => {
      const button = document.createElement('button');
      const image = document.createElement('img');
      button.type = 'button';
      button.className = 'yagcho-media-gallery__thumbnail';
      button.setAttribute('aria-current', String(index === this.activeIndex));
      button.setAttribute('aria-label', thumbnailLabel.replace('__index__', index + 1));
      button.addEventListener('click', () => this.showItem(index));
      image.alt = '';
      image.decoding = 'async';
      image.height = 96;
      image.loading = index === this.activeIndex ? 'eager' : 'lazy';
      image.src = item.src;
      image.width = 96;
      button.append(image);
      this.thumbnails.append(button);
    });

    const activeThumbnail = this.thumbnails.querySelector('[aria-current="true"]');
    activeThumbnail?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }

  updateControls() {
    const hasMultipleItems = this.items.length > 1;
    this.previousButton.disabled = !hasMultipleItems;
    this.nextButton.disabled = !hasMultipleItems;
    this.previousButton.hidden = !hasMultipleItems;
    this.nextButton.hidden = !hasMultipleItems;
  }
}

if (!customElements.get('yagcho-media-gallery')) {
  customElements.define('yagcho-media-gallery', YagchoMediaGallery);
}
